import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { ShareButtons } from "../components/ShareButtons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Events = () => {
    const [events, setEvents] = useState([]);
    const [email, setEmail] = useState("");
    const [subLoading, setSubLoading] = useState(false);

    useEffect(() => {
        axios.get(`${API}/events`).then(r => setEvents(r.data)).catch(() => {});
    }, []);

    // Shared links point at /events#event-<id>; scroll there once the list has loaded
    useEffect(() => {
        if (!events.length || !window.location.hash) return;
        const target = document.getElementById(window.location.hash.slice(1));
        if (target) setTimeout(() => target.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }, [events]);

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setSubLoading(true);
        try {
            const res = await axios.post(`${API}/subscribe`, { email });
            toast.success(res.data.message || "Subscribed!");
            setEmail("");
        } catch (err) {
            toast.error("Something went wrong. Try again.");
        } finally {
            setSubLoading(false);
        }
    };

    const formatDate = (dateStr, dateNote) => {
        const d = new Date(dateStr + "T00:00:00");
        const valid = !isNaN(d);
        return {
            month: valid ? d.toLocaleDateString("en-US", { month: "short" }) : "TBD",
            day: valid ? d.toLocaleDateString("en-US", { day: "numeric" }) : "—",
            full: dateNote || (valid ? d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : "Date to be announced"),
        };
    };

    return (
        <div className="pt-16">
            {/* Hero */}
            <section className="py-20 px-4 bg-[#3D2008]" data-testid="events-hero">
                <div className="max-w-7xl mx-auto">
                    <span className="font-marker text-[#C9A84C] text-2xl -rotate-1 inline-block mb-4">What's Happening</span>
                    <h1 className="font-anton text-5xl sm:text-7xl lg:text-8xl text-[#F5EDD6] uppercase leading-none mb-4">Events</h1>
                    <p className="font-manrope text-[#F5EDD6]/70 text-xl max-w-2xl">
                        Come out, get involved, and help build momentum for Seeley Lake's skatepark. Events are free and open to everyone.
                    </p>
                </div>
            </section>

            {/* Events Grid */}
            <section className="py-20 px-4 bg-[#F5EDD6]" data-testid="events-list-section">
                <div className="max-w-7xl mx-auto">
                    {events.length === 0 ? (
                        <div className="text-center py-20">
                            <Calendar size={48} className="text-[#C4522A] mx-auto mb-4" />
                            <p className="font-manrope text-[#5C4A35] text-xl">More events coming soon. Stay tuned!</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {events.map((event, i) => {
                                const d = formatDate(event.date, event.date_note);
                                return (
                                    <div key={event.id || i}
                                        id={`event-${event.id}`}
                                        data-testid={`event-item-${i}`}
                                        className={`scroll-mt-28 flex flex-col sm:flex-row gap-4 sm:gap-6 bg-white border-2 border-[#3D2008] p-5 sm:p-6 shadow-[6px_6px_0px_0px_rgba(45,90,39,0.25)] hover:shadow-[8px_8px_0px_0px_rgba(45,90,39,0.45)] hover:-translate-y-1 transition-all duration-200 ${i % 3 === 1 ? "md:mt-6" : ""}`}>
                                        {/* Date badge — always derived from the event date so free-text notes never stretch it */}
                                        <div className="flex-shrink-0 flex items-start gap-3 sm:block">
                                            <div className="w-[72px] text-center border-2 border-[#3D2008]">
                                                <div className="bg-[#C4522A] py-1.5 px-2">
                                                    <span className="font-manrope text-white text-xs font-bold uppercase leading-tight block">{d.month}</span>
                                                </div>
                                                <div className="py-2 px-2">
                                                    <span className="font-anton text-[#3D2008] text-xl leading-none block">{d.day}</span>
                                                </div>
                                            </div>
                                            <h3 className="sm:hidden font-anton text-xl text-[#3D2008] uppercase leading-tight pt-1">{event.title}</h3>
                                        </div>
                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="hidden sm:block font-anton text-xl text-[#3D2008] uppercase mb-2">{event.title}</h3>
                                            {event.image_url && <img src={event.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${event.image_url}` : event.image_url} alt={event.title} className="w-full h-40 object-cover border-2 border-[#3D2008] mb-3" />}
                                            <p className="font-manrope text-sm text-[#5C4A35] leading-relaxed mb-4">{event.description}</p>
                                            <div className="space-y-1">
                                                <div className="flex items-start gap-2">
                                                    <Calendar size={13} className="text-[#C4522A] mt-0.5 flex-shrink-0" />
                                                    <span className="font-manrope text-xs text-[#5C4A35]">{d.full}</span>
                                                </div>
                                                {event.location && (
                                                    <div className="flex items-center gap-2">
                                                        <MapPin size={13} className="text-[#4A7FA5]" />
                                                        <span className="font-manrope text-xs text-[#4A7FA5] font-semibold">{event.location}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="mt-4 pt-3 border-t border-[#E5D9BD]">
                                                <ShareButtons
                                                    compact
                                                    label="Share"
                                                    testId={`event-share-${i}`}
                                                    url={`${window.location.origin}/events#event-${event.id}`}
                                                    title={event.title}
                                                    text={`${event.title} — ${d.full}${event.location ? ` at ${event.location}` : ""}. Come support the Seeley Lake Skatepark!`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {/* Stay Informed */}
            <section className="py-16 px-4 bg-[#2D5A27]" data-testid="events-subscribe-section">
                <div className="max-w-3xl mx-auto text-center">
                    <Mail size={32} className="text-[#C9A84C] mx-auto mb-4" />
                    <h2 className="font-anton text-4xl text-[#F5EDD6] uppercase mb-4">Stay in the Loop</h2>
                    <p className="font-manrope text-[#F5EDD6]/70 text-lg mb-8">
                        Get updates on upcoming events, fundraising milestones, and park news sent straight to your inbox.
                    </p>
                    <form onSubmit={handleSubscribe} data-testid="events-subscribe-form" className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Your email address"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            required
                            data-testid="events-email-input"
                            className="flex-1 h-12 px-4 bg-white border-2 border-[#3D2008] font-manrope text-[#3D2008] placeholder-[#5C4A35]/50 focus:outline-none focus:border-[#C4522A]"
                        />
                        <button
                            type="submit"
                            disabled={subLoading}
                            data-testid="events-subscribe-btn"
                            className="px-6 py-3 bg-[#C4522A] text-white font-anton uppercase tracking-wider border-2 border-[#C9A84C] shadow-[4px_4px_0px_0px_rgba(201,168,76,0.5)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform duration-150 disabled:opacity-60"
                        >
                            {subLoading ? "..." : "Subscribe"}
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
};

export default Events;
